import Seo, { faqJsonLd } from '@/components/Seo';
import { Reveal, Parallax } from '@/components/motion';
import Faq from '@/components/Faq';
import FlowDiagram, { ServiceGlyph } from '@/components/diagrams';
import { CtaSection } from '@/components/PageHero';

export default function ServicePageLayout({
  seo,
  group,
  headline,
  lede,
  audienceIntro,
  audience,
  image,
  imageAlt,
  problems,
  capabilities,
  diagram,
  diagramCaption,
  helps,
  helpsIntro,
  faqs,
  cta,
  interestParam,
  note,
  variant = 'a',
}) {
  const slug = seo.path.split('/').pop();
  return (
    <>
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={faqJsonLd(faqs)} />

      <section className="relative overflow-hidden bg-deep" data-testid={`${seo.path}-hero`}>
        <div className="aura aura--emerald" style={{ width: 620, height: 620, top: -260, right: -160 }} />
        <div className="wrap pt-40 pb-20 lg:pt-48 lg:pb-24 relative">
          <Reveal>
            <p className="eyebrow-mono">{group}</p>
            <h1 className="h1 mt-7 max-w-[24ch]">{headline}</h1>
            <p className="lede mt-8 max-w-[58ch]">{lede}</p>
          </Reveal>
        </div>
        <div
          className="absolute bottom-0 inset-x-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(178,139,67,0.5), transparent)' }}
        />
      </section>

      <section className="bg-deep section" data-testid="service-audience">
        <div className="wrap grid lg:grid-cols-2 gap-14 items-center">
          <div className={variant === 'b' ? 'lg:order-2' : ''}>
            <Reveal>
              <p className="eyebrow-mono">Who This Serves</p>
              <h2 className="h2 mt-6 max-w-[20ch]">{audienceIntro}</h2>
              <ul className="mt-9 max-w-xl">
                {audience.map((a) => (
                  <li key={a} className="cap-item">
                    <span className="dot" />
                    <span className="text-[16px]">{a}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className={'relative ' + (variant === 'b' ? 'lg:order-1' : '')}>
            <Parallax amount={14}>
              <div
                className="tick-frame rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(178,139,67,0.3)' }}
              >
                <span className="tick tick--tr" /><span className="tick tick--bl" />
                <img
                  src={image}
                  alt={imageAlt}
                  width="1400"
                  height="900"
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover"
                  style={{ filter: 'saturate(0.78) brightness(0.82)' }}
                />
              </div>
            </Parallax>
          </div>
        </div>
      </section>

      <section className="bg-card border-y border-gold/15 section" data-testid="service-problems">
        <div className="wrap grid lg:grid-cols-[1fr_1.6fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">Common Friction</p>
            <h2 className="h2 mt-6 max-w-[16ch]">Problems we help address.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              {problems.map((p, i) => (
                <div className="prob-row" key={p.t}>
                  <span className="n">0{i + 1}</span>
                  <div>
                    <h4>{p.t}</h4>
                    <p>{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-deep section" data-testid="service-capabilities">
        <div className="wrap grid lg:grid-cols-[1fr_1.6fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">Scope</p>
            <h2 className="h2 mt-6 max-w-[16ch]">What KTK evaluates and coordinates.</h2>
            <div className="mt-10">
              <ServiceGlyph name={slug} className="w-24 h-24 opacity-80" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass p-8 lg:p-10">
              <div className="sm:columns-2 gap-10">
                {capabilities.map((c) => (
                  <div key={c} className="cap-item break-inside-avoid">
                    <span className="dot" />
                    <span className="text-[15.5px]">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-card border-y border-gold/15 section" data-testid="service-diagram">
        <div className="wrap">
          <Reveal>
            <p className="eyebrow-mono">How the Pieces Fit</p>
            <h2 className="h2 mt-6 max-w-[22ch]">{diagram.title}</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-14">
              <FlowDiagram steps={diagram.steps} />
            </div>
            {diagramCaption && (
              <p className="text-[14px] text-mist mt-8 max-w-[70ch]">{diagramCaption}</p>
            )}
          </Reveal>
          {note && (
            <Reveal delay={0.16}>
              <div
                className="mt-12 p-7 max-w-3xl rounded-2xl"
                style={{ border: '1px solid rgba(212,175,55,0.4)', background: 'rgba(178,139,67,0.08)' }}
                data-testid="service-note"
              >
                <p className="text-[14px] leading-relaxed text-ivory/90">{note}</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-deep section" data-testid="service-helps">
        <div className="wrap grid lg:grid-cols-[1fr_1.6fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">Working with KTK</p>
            <h2 className="h2 mt-6 max-w-[16ch]">How KTK helps.</h2>
            <p className="lede mt-6 max-w-[44ch]">{helpsIntro}</p>
          </Reveal>
          <div>
            {helps.map((h, i) => (
              <Reveal key={h.t} delay={i * 0.06}>
                <div className="py-8 border-t border-gold/15 last:border-b grid sm:grid-cols-[56px_1fr] gap-4 items-baseline">
                  <span className="font-mono text-[12px] text-goldbright">0{i + 1}</span>
                  <div>
                    <h3 className="font-semibold text-[17.5px] text-ivory">{h.t}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-mist max-w-[64ch]">{h.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card border-y border-gold/15 section" data-testid="service-faqs">
        <div className="wrap grid lg:grid-cols-[1fr_1.6fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">Common Questions</p>
            <h2 className="h2 mt-6 max-w-[16ch]">Straight answers.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Faq items={faqs} baseId={seo.path.split('/').filter(Boolean).join('-') + '-faq'} />
          </Reveal>
        </div>
      </section>

      <CtaSection
        title={cta.title}
        copy={cta.copy}
        buttonLabel={cta.button}
        buttonTo="/contact"
        testid="service-cta-button"
        interestParam={interestParam}
      />
    </>
  );
}
